import React, { useState } from 'react';
import { ModalBase, ModalBaseProps } from '../../modalBase';
import './aiModal.scss';
import Icon, { IconType } from '../../../icon/Icon';
import Tag from '../../../tag/Tag';
import InputField from '../../../field/input/InputField';
import Button, { ButtonType } from '../../../button/Button';
import { createConversation, sendMessageToApi } from './aiModalApi';

type AIModalProps = Omit<
  ModalBaseProps,
  'showCloseButton' | 'children' | 'title'
> & {
  OnShowResults?: (filters: any[]) => void;
};

type Message = {
  sender: 'user' | 'ai';
  content: React.ReactNode;
  filters?: any[];
};

export const AIModal: React.FC<AIModalProps> = ({ onClose, ...rest }) => {
  const [inputValue, setInputValue] = useState('');
  const [conversation, setConversation] = useState<Message[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [amount, setAmount] = useState(0);

  const appendMessage = (msg: Message) => {
    setConversation((prev) => [...prev, msg]);
  };

  const handleReset = () => {
    console.log('Resetting AI conversation.');
    setConversation([]);
    setInputValue('');
    setConversationId(null);
    setAmount(0);
  };

  const formatAIResponse = (reply: any) => {
    if (!reply?.cody_response) {
      return { content: <>No response from AI.</>, filters: [] };
    }

    setAmount(reply.total_results_found || 0);
    return {
      content: <p>{reply.cody_response}</p>,
      filters: reply.filters || [],
    };
  };

  const handleSendMessage = async (message?: string) => {
    const trimmed = (message ?? inputValue).trim();
    if (!trimmed || isLoading) return;

    setAmount(0);
    appendMessage({ sender: 'user', content: trimmed });
    setInputValue('');
    setIsLoading(true);

    try {
      let currentId = conversationId;

      if (!currentId) {
        const newConv = await createConversation();
        currentId = newConv?.conversation_id ?? null;
        if (!currentId) {
          appendMessage({
            sender: 'ai',
            content: <>Failed to initiate conversation.</>,
          });
          setIsLoading(false);
          return;
        }
        setConversationId(currentId);
      }

      const reply = await sendMessageToApi(currentId, trimmed);
      const { content, filters } = formatAIResponse(reply);
      appendMessage({ sender: 'ai', content, filters });
    } catch {
      appendMessage({
        sender: 'ai',
        content: <>An error occurred while communicating with AI.</>,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTagClick = (prompt: string) => {
    setInputValue(prompt);
    handleSendMessage(prompt);
  };

  const examplePrompts = [
    rest.labels?.conversationExample1 ||
      'I need a system for a 4m high wall for residential projects',
    rest.labels?.conversationExample2 ||
      'Show me fire-rated partition systems with severe acoustic performance',
    rest.labels?.conversationExample3 ||
      'What are the best systems for hospitals?',
  ];

  const renderFilters = (filters: any[], index: number) => (
    <>
      <br />
      <p>
        {rest.labels?.identifiedFiltersConversationPart ||
          'I’ve identified the following requirements already:'}
      </p>
      <div>
        {filters.map((filter, i) => (
          <Tag
            key={filter.id || i}
            id={filter.id || i}
            label={`${filter.attribute}: ${filter.value}`}
            isFacet
            isLoading={isLoading}
            onClick={() => {
              const updatedFilters = filters.filter(
                (f) => f.attribute !== filter.attribute
              );
              setConversation((prev) =>
                prev.map((msg, idx) =>
                  idx === index ? { ...msg, filters: updatedFilters } : msg
                )
              );
              setAmount(0);
            }}
          />
        ))}
      </div>
    </>
  );

  const getLatestFilters = () => {
    for (let i = conversation.length - 1; i >= 0; i--) {
      const msg = conversation[i];
      if (msg.sender === 'ai' && msg.filters && msg.filters.length > 0) {
        return msg.filters;
      }
    }
    return [];
  };

  const renderConversation = () => (
    <div className="ai-conversation">
      {conversation.map((msg, index) => {
        const isLastAI =
          msg.sender === 'ai' &&
          conversation.slice(index + 1).find((m) => m.sender === 'ai') ===
            undefined &&
          conversation[conversation.length - 1].sender === 'ai';

        return (
          <React.Fragment key={index}>
            <div
              className={`ai-message ${
                msg.sender === 'user' ? 'ai-user-message' : 'ai-ai-message'
              }`}
            >
              {msg.sender === 'ai' ? (
                <>
                  <div className="ai-icon-panel">
                    <Icon iconType={IconType.AiChat} className="icon" />
                  </div>
                  <div className="ai-message-content">
                    {msg.content}
                    {(msg.filters ?? []).length > 0 &&
                      renderFilters(msg.filters ?? [], index)}
                  </div>
                </>
              ) : (
                <div>{msg.content}</div>
              )}
            </div>

            {isLastAI && (
              <div className="ai-extra-content">
                <p>
                  Currently there are {amount} results matching. You can view
                  the results or provide us with more information.
                </p>
                <Button
                  label={`${
                    rest.labels?.showResultsButton || 'Show results '
                  } (${amount})`}
                  icon={IconType.Show}
                  type={ButtonType.PRIMARY}
                  onClick={() => {
                    if (rest.OnShowResults) {
                      if (rest.OnShowResults) {
                        const filters = getLatestFilters();
                        console.log('Filters:', filters);
                        rest.OnShowResults?.(filters);
                      }
                      onClose();
                    }
                    onClose();
                  }}
                />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  return (
    <ModalBase
      {...rest}
      onClose={onClose}
      showCloseButton={false}
      closeOnEsc
      closeOnOverlayClick
    >
      <div className="ai-wrapper">
        <div className="ai-modal-header">
          <div className="ai-modal-center">
            <Icon
              iconType={IconType.AiChat}
              className="u-icon-large ai-color"
            />
            <h4 className="ai-modal-title ai-color">
              {rest.labels.searchWindowTitle || 'AI-Powered Search (beta)'}
            </h4>
          </div>
          {conversation.length > 0 && (
            <div className="ai-reset-button">
              <Button
                label={rest.labels?.resetButton || 'Reset'}
                icon={IconType.Restart}
                type={ButtonType.TERTIARY}
                onClick={handleReset}
              />
            </div>
          )}
        </div>

        <hr className="ai-modal-divider" />

        <div className="ai-modal-content ai-color">
          {conversation.length === 0 ? (
            <div className="ai-intro-content">
              <p className="ai-content-header">
                {rest.labels?.conversationWindowPlaceholder ||
                  'Ask questions and get enhanced answers with AI'}
              </p>
              <span className="ai-tags ai-color">
                {[0, 1, 2].map((idx) => (
                  <Tag
                    key={idx}
                    id={idx.toString()}
                    label={examplePrompts[idx]}
                    icon={{
                      iconType: IconType.AiChat,
                      className: 'u-icon-small',
                    }}
                    onClick={() => handleTagClick(examplePrompts[idx])}
                  />
                ))}
              </span>
            </div>
          ) : (
            renderConversation()
          )}
        </div>

        <hr className="ai-modal-divider" />

        <div className="ai-modal-footer">
          <div className="input-wrapper">
            <InputField
              placeholder={
                conversation.length
                  ? rest.labels?.conversationInputPlaceholder2 ||
                    'Write a message'
                  : rest.labels?.conversationInputPlaceholder1 ||
                    'Search in natural language'
              }
              id="ai-searchfield"
              onChange={(e) => setInputValue(e.target.value)}
              value={inputValue}
              submitLabel={rest.labels?.sendButton || 'Send'}
              type="search"
              onSubmit={() => handleSendMessage()}
              onClear={() => setInputValue('')}
            ></InputField>
          </div>
          <Button
            label={`${
              rest.labels?.showResultsButton || 'Show results '
            } (${amount})`}
            icon={IconType.Show}
            type={ButtonType.PRIMARY}
            onClick={() => {
              if (rest.OnShowResults) {
                const filters = getLatestFilters();
                console.log('Filters:', filters);
                rest.OnShowResults?.(filters);
              }
              onClose();
            }}
          />
        </div>
      </div>
    </ModalBase>
  );
};
export default AIModal;
